/**
 * LifestylePatternsCard — observational, non-diagnostic correlation card.
 *
 * Surfaces gentle patterns from the user's last 30 days of daily check-ins,
 * pulled from GET /api/insights/lifestyle-patterns. Stays hidden until the
 * backend reports ≥7 days of data. Strictly descriptive copy — never
 * clinical language or risk scores.
 *
 * Drop-in: import and render anywhere on the Insights page. Self-contained
 * fetching + loading + empty states.
 */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Leaf, Sparkles, TrendingDown, TrendingUp, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRIC_ICON = {
  sleep:     <span className="text-indigo-600">🌙</span>,
  energy:    <span className="text-amber-600">⚡</span>,
  hydration: <span className="text-cyan-600">💧</span>,
};

const LifestylePatternsCard = () => {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/insights/lifestyle-patterns`, { withCredentials: true });
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: 'Could not load patterns' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const { loading, data, error } = state;

  // Don't render anything until the user has a meaningful baseline of
  // check-ins — preserves the calm UX rather than showing an empty card.
  if (loading || error) return null;
  if (!data) return null;

  if (!data.ready) {
    return (
      <Card className="border border-teal-100 bg-teal-50/40 rounded-2xl shadow-sm" data-testid="lifestyle-patterns-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-teal-900">
            <Leaf className="h-4 w-4 text-teal-600" />
            Lifestyle patterns — coming soon
          </CardTitle>
          <CardDescription className="text-xs text-teal-800/80">
            We'll surface gentle observations after you've completed {data.min_required} daily check-ins
            ({data.sample_size}/{data.min_required} so far).
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!data.patterns || data.patterns.length === 0) {
    return (
      <Card className="border border-teal-100 bg-teal-50/40 rounded-2xl shadow-sm" data-testid="lifestyle-patterns-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-teal-900">
            <Leaf className="h-4 w-4 text-teal-600" />
            Lifestyle patterns
          </CardTitle>
          <CardDescription className="text-xs text-teal-800/80">
            Your recent check-ins don't show any notable lifestyle patterns yet —
            that's perfectly fine. Keep logging and we'll surface gentle observations as they emerge.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="border border-teal-200 bg-gradient-to-br from-teal-50/60 to-cyan-50/40 rounded-2xl shadow-sm" data-testid="lifestyle-patterns-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-teal-900">
          <Sparkles className="h-4 w-4 text-teal-600" />
          Lifestyle patterns
        </CardTitle>
        <CardDescription className="text-xs text-teal-800/80">
          Gentle observations from your last {data.sample_size} daily check-ins.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="space-y-2.5" data-testid="lifestyle-patterns-list">
          {data.patterns.map((p, i) => (
            <li
              key={`${p.habit}-${p.metric}-${i}`}
              className="rounded-xl bg-white/80 border border-teal-100 px-3 py-2.5"
              data-testid={`pattern-${p.habit}-${p.metric}`}
            >
              <div className="flex items-start gap-2.5">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-50 border border-teal-100 flex items-center justify-center text-base" aria-hidden="true">
                  {METRIC_ICON[p.metric] || <Leaf className="h-3.5 w-3.5 text-teal-600" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 leading-snug">{p.headline}</p>
                  <p className="text-[11px] text-gray-500 leading-snug mt-1">{p.supporting}</p>
                </div>
                <span className="flex-shrink-0 mt-0.5" aria-hidden="true">
                  {p.delta < 0
                    ? <TrendingDown className="h-3.5 w-3.5 text-rose-500" />
                    : <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />}
                </span>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex items-start gap-1.5 text-[11px] text-teal-800/80 italic" data-testid="lifestyle-patterns-disclaimer">
          <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>{data.disclaimer}</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default LifestylePatternsCard;
